import { QueryClient } from '@tanstack/react-query'

import config from '@config'
import { getCurrentViewportType } from '@measures/responsive'
import { getPageMetadata } from '@hooks/usePageMetadata'
import { User } from '@hooks/useUser'
import {
  getSubscriptionStatus,
  SubscriptionStatus,
} from '@hooks/useSubscriptionStatus'
import { GetURLParam, getURLParam } from '@utils/urlParams'

const {
  publication: { publication },
} = config

type UtmAttributes = {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
}

type CommonAttributes = {
  url: string
  path: string
  host: string
  query: string
  browser: 'chrome' | 'firefox' | 'safari' | 'edge' | 'unknown' | null
  deviceType: 'mobile' | 'desktop' | null
} & UtmAttributes

type AdditionalAttributes = {
  publication: 'fr' | 'de'
  requestSource: 'mobile' | 'desktop' | 'tablet' | ''
  articleFeatures: string | undefined
  documentReferrer: string | undefined
}

type UserRelatedAttributes = {
  loggedIn: boolean
  subscriptionStatus: SubscriptionStatus
}

export type GrowthBookAttributes = CommonAttributes &
  AdditionalAttributes &
  UserRelatedAttributes

const getDeviceType = (): CommonAttributes['deviceType'] | null => {
  if (typeof navigator === 'undefined') {
    return null
  }

  return navigator.userAgent.match(/Mobi/) ? 'mobile' : 'desktop'
}

const getBrowser = (): CommonAttributes['browser'] | null => {
  if (typeof navigator === 'undefined') {
    return null
  }

  const userAgent = navigator.userAgent

  return userAgent.match(/Edg/)
    ? 'edge'
    : userAgent.match(/Chrome/)
      ? 'chrome'
      : userAgent.match(/Firefox/)
        ? 'firefox'
        : userAgent.match(/Safari/)
          ? 'safari'
          : 'unknown'
}

const getUtmAttributes = (queryClient: QueryClient) => {
  const utmAttributes: UtmAttributes = {}

  const utmAttributeToUrlParamMap: Record<
    keyof UtmAttributes,
    Parameters<GetURLParam>[0]
  > = {
    utmSource: 'utm_source',
    utmMedium: 'utm_medium',
    utmCampaign: 'utm_campaign',
    utmTerm: 'utm_term',
    utmContent: 'utm_content',
  }

  const utmAttributesList = Object.keys(
    utmAttributeToUrlParamMap
  ) as (keyof UtmAttributes)[]

  utmAttributesList.forEach((utmAttribute) => {
    const urlParamName = utmAttributeToUrlParamMap[utmAttribute]
    const urlParam = getURLParam(urlParamName, queryClient)

    if (urlParam) {
      utmAttributes[utmAttribute] = urlParam
    }
  })

  return utmAttributes
}

const getCommonAttributes = (
  queryClient: QueryClient
): Partial<CommonAttributes> | CommonAttributes => {
  if (typeof window === 'undefined') {
    return {} as Partial<CommonAttributes>
  }

  return {
    url: window?.location?.href,
    path: window?.location?.pathname,
    host: window?.location?.host,
    query: window?.location?.search,
    browser: getBrowser(),
    deviceType: getDeviceType(),
    ...getUtmAttributes(queryClient),
  }
}

const getAdditionalAttributes = (
  queryClient: QueryClient
): AdditionalAttributes => {
  return {
    publication: (publication === 'romandie' ? 'fr' : 'de') as 'fr' | 'de',
    requestSource: getCurrentViewportType(),
    articleFeatures: getPageMetadata(queryClient)?.articleFeatures?.toString(),
    documentReferrer:
      typeof document === 'undefined' ? undefined : document.referrer,
  }
}

const getUserRelatedAttributes = (
  queryClient: QueryClient
): UserRelatedAttributes => {
  const userData = queryClient.getQueryData<User>(['user'])
  const subscriptionStatus = getSubscriptionStatus(queryClient)

  return {
    loggedIn: !!userData,
    subscriptionStatus,
  }
}

const getAttributesforGrowthBook = (queryClient: QueryClient) => ({
  ...getCommonAttributes(queryClient),
  ...getAdditionalAttributes(queryClient),
  ...getUserRelatedAttributes(queryClient),
})

export { getAttributesforGrowthBook }
